/** Voice Library when the platform voice engine is Amazon Nova Sonic (set by the super admin). */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Mic, Play, Square, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface NovaVoice {
  voice_id: string;
  name: string;
  description: string;
  locale: string;
  polyglot: boolean;
  labels: { language: string; gender: string; accent: string };
  preview_url: string;
  saved?: boolean;
}

interface NovaLanguages {
  india: boolean;
  defaultLanguage: string;
  languages: Array<{ code: string; name: string; voiceCount: number }>;
}

export default function NovaSonicVoiceLibrary() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("browse-library");
  const [language, setLanguage] = useState("");
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Languages are ordered by billing country: Indian accounts see Hindi and Indian English first.
  const { data: languageInfo } = useQuery<NovaLanguages>({ queryKey: ["/api/agents/nova-sonic/languages"], staleTime: 60_000 });
  useEffect(() => {
    if (!language && languageInfo) setLanguage(languageInfo.defaultLanguage);
  }, [language, languageInfo]);

  const { data: catalog = [], isLoading: loadingCatalog } = useQuery<NovaVoice[]>({
    queryKey: ["/api/agents/nova-sonic/voices", language],
    queryFn: async () => (await apiRequest("GET", `/api/agents/nova-sonic/voices?language=${encodeURIComponent(language)}`)).json(),
    enabled: !!language,
    staleTime: 60_000,
  });

  const { data: mine = [], isLoading: loadingMine } = useQuery<NovaVoice[]>({
    queryKey: ["/api/agents/nova-sonic/user-voices"],
    queryFn: async () => (await apiRequest("GET", "/api/agents/nova-sonic/user-voices")).json(),
    staleTime: 0,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/agents/nova-sonic/user-voices"] });
    queryClient.invalidateQueries({ queryKey: ["/api/agents/nova-sonic/voices"] });
  };

  const add = useMutation({
    mutationFn: async (voice: NovaVoice) => (await apiRequest("POST", "/api/agents/nova-sonic/voices/add", { voiceId: voice.voice_id, locale: voice.locale })).json(),
    onSuccess: (_d, voice) => {
      toast({ title: "Voice added", description: `${voice.name} is in your library and listed first when you create an agent.` });
      refresh();
    },
    onError: (e: Error) => toast({ title: "Could not add the voice", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (voice: NovaVoice) => (await apiRequest("DELETE", `/api/agents/nova-sonic/user-voices/${voice.voice_id}`)).json(),
    onSuccess: (_d, voice) => {
      toast({ title: "Voice removed", description: `${voice.name} has been removed from your library.` });
      refresh();
    },
    onError: (e: Error) => toast({ title: "Could not remove the voice", description: e.message, variant: "destructive" }),
  });

  useEffect(() => () => audioRef.current?.pause(), []);

  const togglePreview = (voice: NovaVoice) => {
    if (playing === voice.voice_id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(voice.preview_url);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      setPlaying(null);
      toast({ title: "Preview failed", description: "Could not play the sample for this voice.", variant: "destructive" });
    };
    audioRef.current = audio;
    setPlaying(voice.voice_id);
    void audio.play().catch(() => setPlaying(null));
  };

  const languageName = useMemo(() => languageInfo?.languages.find((l) => l.code === language)?.name ?? "", [languageInfo, language]);
  const shown = tab === "my-voices" ? mine : catalog;
  const loading = tab === "my-voices" ? loadingMine : loadingCatalog || !language;

  // A render function, not a component: a component defined here would remount every card on each render.
  const renderCard = (voice: NovaVoice) => {
    const female = voice.labels.gender === "female";
    const border = female ? "border-pink-200 dark:border-pink-800/50" : "border-blue-200 dark:border-blue-800/50";
    const bar = female ? "bg-gradient-to-r from-pink-400 via-rose-500 to-pink-400" : "bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400";
    const isPlaying = playing === voice.voice_id;
    return (
      <Card key={`${voice.voice_id}-${voice.locale}`} className={`p-4 hover-elevate relative overflow-visible ${border}`} data-testid={`nova-voice-${voice.voice_id}-${voice.locale}`}>
        <div className={`absolute top-0 left-0 right-0 h-1 ${bar} rounded-t-lg`} />
        <h3 className="font-semibold truncate mb-1.5">{voice.name}</h3>
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{voice.description}</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className="text-xs capitalize">{voice.labels.gender}</Badge>
          <Badge variant="outline" className="text-xs">{voice.locale}</Badge>
          {voice.polyglot && <Badge className="text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0">Multilingual</Badge>}
        </div>
        <div className="space-y-2 mt-3">
          <Button
            variant={isPlaying ? "default" : "outline"}
            size="sm"
            onClick={() => togglePreview(voice)}
            className={isPlaying ? "w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white" : "w-full border-green-300 hover:bg-green-50 dark:hover:bg-green-950"}
          >
            {isPlaying ? (<><Square className="h-4 w-4 mr-2" />Stop Preview</>) : (<><Play className="h-4 w-4 mr-2" />Test Voice</>)}
          </Button>
          <div className="flex items-center justify-end gap-2">
            {tab === "my-voices" ? (
              <Button size="sm" variant="outline" onClick={() => remove.mutate(voice)} disabled={remove.isPending} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-1.5" />Remove
              </Button>
            ) : voice.saved ? (
              <Badge variant="outline" className="text-green-600 border-green-300">✓ Added</Badge>
            ) : (
              <Button size="sm" onClick={() => add.mutate(voice)} disabled={add.isPending} className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                + Add
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-pink-50 via-rose-100/50 to-fuchsia-50 dark:from-pink-950/40 dark:via-rose-900/30 dark:to-fuchsia-950/40 border border-pink-100 dark:border-pink-900/50 p-6 md:p-8">
        <div className="relative flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/25">
            <Mic className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t("voices.title")}</h1>
            <p className="text-muted-foreground mt-0.5">
              {loading ? "Loading voices..." : `${shown.length} ${shown.length === 1 ? "voice" : "voices"} ${tab === "my-voices" ? "in your library" : `for ${languageName || "this language"}`}`}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="my-voices" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">My Voices</TabsTrigger>
          <TabsTrigger value="browse-library" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">Browse Library</TabsTrigger>
        </TabsList>

        {tab === "browse-library" && languageInfo && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Language:</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[240px]" data-testid="select-nova-library-language">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {languageInfo.languages.map((l) => (
                  <SelectItem key={l.code} value={l.code}>{l.name} ({l.voiceCount})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {languageInfo.india ? "Indian languages are listed first for your billing country." : "English is listed first; Hindi and the other languages are below."}
            </span>
          </div>
        )}

        <TabsContent value="my-voices" className="mt-4" />
        <TabsContent value="browse-library" className="mt-4" />

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-3" />
                <Skeleton className="h-9 w-full" />
              </Card>
            ))}
          </div>
        ) : shown.length === 0 ? (
          <Card className="p-16 text-center">
            <Mic className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">{tab === "my-voices" ? "No voices in your library yet" : "No voices found"}</h3>
            <p className="text-muted-foreground">
              {tab === "my-voices" ? "Open Browse Library, test a voice and press + Add. Added voices are listed first when you create an agent." : "Try another language."}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {shown.map((voice) => renderCard(voice))}
          </div>
        )}
      </Tabs>

      <p className="text-xs text-muted-foreground">
        These are fixed voices. Voice cloning and pitch or accent changes are not available; ask your administrator to switch the voice engine if you need a cloned voice.
      </p>
    </div>
  );
}
